import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { keys } from '@/api/queryKeys';
import { normalizeFormListRecords } from '../adapters/formsAdapters';
import { formsApi } from '../api/formsApi';
import type { FormAssignInput, FormTemplateDetail, SendFormInput } from '../api/types';

export function useFormTemplates() {
  return useQuery({
    queryKey: keys.forms.templates,
    queryFn: async ({ signal }) => {
      const response = await formsApi.getTemplatesList({ signal });
      return {
        ...response,
        results: normalizeFormListRecords(response),
      };
    },
    staleTime: 120_000,
  });
}

export function useFormTemplatesSimple() {
  return useQuery({
    queryKey: keys.forms.templatesSimple,
    queryFn: ({ signal }) => formsApi.getTemplatesSimple({ signal }),
    staleTime: 120_000,
  });
}

export function useCreateFormTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; description?: string }) => formsApi.createTemplate(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.forms.templates });
      void queryClient.invalidateQueries({ queryKey: keys.forms.templatesSimple });
    },
  });
}

export function useUpdateFormTemplate(formId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<FormTemplateDetail>) => formsApi.updateTemplate(formId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.forms.templates });
      void queryClient.invalidateQueries({ queryKey: keys.forms.template(formId) });
    },
  });
}

export function useDeleteFormTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (formId: string) => formsApi.deleteTemplate(formId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.forms.templates });
      void queryClient.invalidateQueries({ queryKey: keys.forms.templatesSimple });
    },
  });
}

export function useSendForm() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SendFormInput) => formsApi.sendForm(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.forms.queue });
      void queryClient.invalidateQueries({ queryKey: keys.formsStats });
    },
  });
}

export function useAssignFormTemplate(formId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: FormAssignInput) => formsApi.assignTemplate(formId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.forms.queue });
      void queryClient.invalidateQueries({ queryKey: keys.formsStats });
    },
  });
}

export function useMintPatientFormLink() {
  return useMutation({
    mutationFn: (assignmentId: string) => formsApi.mintPatientLink(assignmentId),
  });
}
